import { memo, useEffect, useState, FC } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Wrap, WrapItem, Alert, AlertIcon, AlertTitle, AlertDescription, Box, Button } from "@chakra-ui/react";

import { MenuCard } from "../../organisms/user/MenuCard";

import { useLoginUser } from "../../../hooks/useLoginUser";
import { usePasswordValidation } from "../../../hooks/usePasswordValidation";
import { HeaderLayout } from "../../templates/HeaderLayout";
import { PageDiscription } from "../../atoms/button/PageDiscription";
import { menuInfo, menuSet } from "../../../MyRouter";

export const Home: FC = memo(() => {
  const { loginUser, selectTenant } = useLoginUser();
  const { isPasswordExpiredState } = usePasswordValidation();
  const [homeMenu, setHomeMenu] = useState<menuInfo[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  
  // パスワード有効期限切れで自動リダイレクトされた場合はアラートを表示しない
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  
  useEffect(() => {
    // パスワード有効期限切れ状態の場合、最初は警告を表示せず、
    // 一定時間後に表示する（自動リダイレクトの場合は表示されない）
    if (isPasswordExpiredState) {
      const timer = setTimeout(() => {
        setShowPasswordAlert(true);
      }, 1000); // 1秒後に表示
      
      return () => clearTimeout(timer);
    } else {
      setShowPasswordAlert(false);
    }
  }, [isPasswordExpiredState]);

  useEffect(() => {
    if (loginUser) {
      if (loginUser.isAccountAdmin) {
        const menus = menuSet.filter((menu) => menu.allow.accountAdmin);
        const sortMenus = menus.sort((a, b) => a.seq - b.seq);
        setHomeMenu(sortMenus);
      } else if (loginUser.isAdmin) {
        const menus = menuSet.filter((menu) => menu.allow.tenantAdmin);
        const sortMenus = menus.sort((a, b) => a.seq - b.seq);
        setHomeMenu(sortMenus);
      } else {
        const menus = menuSet.filter((menu) => menu.allow.user);
        const sortMenus = menus.sort((a, b) => a.seq - b.seq);
        setHomeMenu(sortMenus);
      }
    } else {
      setHomeMenu([]);
    }
  }, [loginUser, selectTenant]);

  return (
    <>
      <HeaderLayout>
        {loginUser && <Outlet />}
        {useLocation().pathname === "/home" ? (
          <>
            {loginUser ? (
              <>
                {isPasswordExpiredState && showPasswordAlert && (
                  <Box p={4}>
                    <Alert status="warning">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>パスワードの有効期限が切れています！</AlertTitle>
                        <AlertDescription>
                          セキュリティのため、他の機能をご利用いただく前にパスワードの変更が必要です。
                        </AlertDescription>
                        <Button 
                          colorScheme="orange" 
                          size="sm" 
                          mt={2}
                          onClick={() => navigate("/change-password")}
                        >
                          パスワードを変更する
                        </Button>
                      </Box>
                    </Alert>
                  </Box>
                )}
                <PageDiscription>
                  {isPasswordExpiredState 
                    ? "パスワードを変更してください。" 
                    : "メニューを選択してください。"
                  }
                </PageDiscription>
                <Wrap p={{ base: 4, md: 10 }}>
                  {homeMenu.map((menu: menuInfo) => (
                    <WrapItem key={menu.path} mx="auto">
                      <MenuCard
                        menuTitle={menu.caption}
                        description={menu.description}
                        enabled={!isPasswordExpiredState}
                        onClick={() => {
                          if (!isPasswordExpiredState) {
                            navigate(`/home/${menu.path}` as any);
                          }
                        }}
                      />
                    </WrapItem>
                  ))}
                </Wrap>
              </>
            ) : (
              <>
                <PageDiscription>ログインしてください</PageDiscription>
              </>
            )}
          </>
        ) : (
          <></>
        )}
      </HeaderLayout>
    </>
  );
});
